import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';
import { emailsRootKey } from '@/shared/emails/api';
import { useElevation } from '@/shared/elevation/useElevation';
import { sessionQueryKey } from '@/shared/session/currentUser';
import {
  type Guideline,
  type GuidelineFilters,
  guidelineKeys,
  guidelinesApi,
  type GuidelineUpdate,
} from '@/shared/triage/guidelines';

import { adminApi, adminKeys } from './api';
import type {
  AccessLogFilters,
  AdminUser,
  AdminUserCreate,
  AdminUserCreated,
  AdminUserUpdate,
  ConnectionTest,
  DocumentRetentionPolicy,
  ErrorFilters,
  FeedbackAnswerFilters,
  FeedbackFilters,
  InviteRead,
  Mailbox,
  MailboxCredentials,
  MailboxUpdate,
  MailboxWrite,
  ModesWrite,
  ReliabilityFilters,
  RetentionRun,
  TriageActivation,
} from './types';

/**
 * What changes when a mailbox appears, goes, or is given to someone: which mail each
 * person sees. The inbox (its list, rail and mailbox filter) and the session (its list
 * of mailboxes) are read again, or a mailbox registered or assigned in the panel stays
 * invisible in the Bandeja until the page is reloaded. That happened in production on
 * 2026-09-24 with `ventas@`.
 */
const WHO_SEES_WHAT = [emailsRootKey, sessionQueryKey] as const;

export function useAdminUsers() {
  return useQuery({ queryKey: adminKeys.users, queryFn: adminApi.listUsers });
}

export function useAdminMailboxes() {
  return useQuery({ queryKey: adminKeys.mailboxes, queryFn: adminApi.listMailboxes });
}

export function useAdminTriage() {
  return useQuery({ queryKey: adminKeys.triage, queryFn: adminApi.triage });
}

export function useAccessLog(filters: AccessLogFilters) {
  return useQuery({
    queryKey: adminKeys.accessLog(filters),
    queryFn: () => adminApi.accessLog(filters),
    placeholderData: (previous) => previous,
  });
}

export function useRetentionPolicy() {
  return useQuery({ queryKey: adminKeys.documentPolicy, queryFn: adminApi.documentPolicy });
}

export function useRetention() {
  return useQuery({ queryKey: adminKeys.retention, queryFn: adminApi.retention });
}

export function useFeedback(filters: FeedbackFilters) {
  return useQuery({
    queryKey: adminKeys.feedback(filters),
    queryFn: () => adminApi.feedback(filters),
  });
}

export function useErrors(filters: ErrorFilters) {
  return useQuery({ queryKey: adminKeys.errors(filters), queryFn: () => adminApi.errors(filters) });
}

/** Administración → Fiabilidad (contract v10.37): the previous window stays on screen
 * while the next one is computed. */
export function useReliability(filters: ReliabilityFilters) {
  return useQuery({
    queryKey: adminKeys.reliability(filters),
    queryFn: () => adminApi.reliability(filters),
    placeholderData: (previous) => previous,
  });
}

/** The rows of one group, asked for when the group is opened, not before. */
export function useErrorOccurrences(fingerprint: string | null, since: string) {
  return useQuery({
    queryKey: adminKeys.errorOccurrences(fingerprint ?? '', since),
    queryFn: () => adminApi.errorOccurrences(fingerprint ?? '', since),
    enabled: Boolean(fingerprint),
  });
}

export function useFeedbackAnswers(filters: FeedbackAnswerFilters) {
  return useQuery({
    queryKey: adminKeys.feedbackAnswers(filters),
    queryFn: () => adminApi.feedbackAnswers(filters),
    placeholderData: (previous) => previous,
  });
}

export function useFeedbackMailboxes() {
  return useQuery({ queryKey: adminKeys.feedbackMailboxes, queryFn: adminApi.feedbackMailboxes });
}

/**
 * Every write of the panel: wrapped in `withElevation`, so a 403 `elevation_required`
 * becomes the password prompt and a retry, and the relevant list is refetched after.
 */
function useGuarded<TData, TVariables>(
  run: (variables: TVariables) => Promise<TData>,
  invalidate: readonly (readonly string[])[],
  onSuccess?: (data: TData) => void,
) {
  const { withElevation } = useElevation();
  const queryClient = useQueryClient();
  return useMutation<TData, ApiError | Error, TVariables>({
    mutationFn: (variables) => withElevation(() => run(variables)),
    onSuccess: (data) => {
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key });
      onSuccess?.(data);
    },
  });
}

export function useCreateUser(onSuccess?: (user: AdminUserCreated) => void) {
  return useGuarded<AdminUserCreated, AdminUserCreate>(
    adminApi.createUser,
    [adminKeys.users, ...WHO_SEES_WHAT],
    onSuccess,
  );
}

export function useInviteUser(onSuccess?: (invite: InviteRead) => void) {
  return useGuarded<InviteRead, string>(adminApi.inviteUser, [adminKeys.users], onSuccess);
}

export function useUpdateUser() {
  return useGuarded<AdminUser, { id: string; payload: AdminUserUpdate }>(
    ({ id, payload }) => adminApi.updateUser(id, payload),
    [adminKeys.users, ...WHO_SEES_WHAT],
  );
}

export function useResetPassword(onSuccess?: (user: AdminUser) => void) {
  return useGuarded<AdminUser, { id: string; password: string }>(
    ({ id, password }) => adminApi.resetPassword(id, password),
    [adminKeys.users],
    onSuccess,
  );
}

export function useTestMailbox() {
  return useGuarded<ConnectionTest, MailboxCredentials>(adminApi.testMailbox, []);
}

/**
 * «Probar conexión» of a registered mailbox (contract v10.17): nobody types the password
 * again. Tested as it is stored, the row's «Última prueba» and «Estado» change, so the
 * list is read again.
 */
export function useTestSavedMailbox() {
  return useGuarded<ConnectionTest, { id: string; typed?: MailboxUpdate }>(
    ({ id, typed }) => adminApi.testSavedMailbox(id, typed),
    [adminKeys.mailboxes],
  );
}

export function useCreateMailbox(onSuccess?: (mailbox: Mailbox) => void) {
  return useGuarded<Mailbox, MailboxWrite>(
    adminApi.createMailbox,
    [adminKeys.mailboxes, adminKeys.triage, ...WHO_SEES_WHAT],
    onSuccess,
  );
}

/**
 * A new name (contract v10.23) moves the mail, the people who see the mailbox and its
 * modes with it: the inbox, the users list and the session (its list of mailboxes) are
 * read again, or they would keep showing the old name until the next reload.
 */
export function useUpdateMailbox() {
  return useGuarded<Mailbox, { id: string; payload: MailboxUpdate }>(
    ({ id, payload }) => adminApi.updateMailbox(id, payload),
    [adminKeys.mailboxes, adminKeys.triage, adminKeys.users, ...WHO_SEES_WHAT],
  );
}

/** «Borrar definitivamente» (contract v10.14): the row and its modes go, the mail stays. */
export function useDeleteMailbox(onSuccess?: () => void) {
  return useGuarded<void, string>(
    adminApi.deleteMailbox,
    [adminKeys.mailboxes, adminKeys.triage, adminKeys.users, ...WHO_SEES_WHAT],
    onSuccess,
  );
}

/**
 * The triage writes answer with the whole activation as it is now, so the cache is set
 * from the answer instead of refetched. The read-only `/activacion` page keeps its own
 * key and is invalidated.
 */
function useTriageWrite<TVariables>(run: (variables: TVariables) => Promise<TriageActivation>) {
  const queryClient = useQueryClient();
  return useGuarded<TriageActivation, TVariables>(run, [], (data) => {
    queryClient.setQueryData(adminKeys.triage, data);
    void queryClient.invalidateQueries({ queryKey: ['triage', 'activation'] });
  });
}

export function usePutDestinations() {
  return useTriageWrite<Record<string, string[]>>(adminApi.putDestinations);
}

export function usePutFolders() {
  return useTriageWrite<Record<string, string>>(adminApi.putFolders);
}

export function usePutModes() {
  return useTriageWrite<ModesWrite>(adminApi.putModes);
}

export function useAllShadow() {
  return useTriageWrite<void>(() => adminApi.allShadow());
}

export function useRunRetention() {
  return useGuarded<RetentionRun, void>(
    () => adminApi.runRetention(),
    [adminKeys.retention, ['admin', 'access-log']],
  );
}

export function usePutRetentionPolicy() {
  const queryClient = useQueryClient();
  return useGuarded<DocumentRetentionPolicy, Record<string, number>>(
    adminApi.putDocumentPolicy,
    [],
    (policy) => queryClient.setQueryData(adminKeys.documentPolicy, policy),
  );
}

/**
 * «Lo que ha aprendido» (contract v10.35): one page of criteria with this status. The
 * previous page stays on screen while the next one loads, as in the access log.
 */
export function useGuidelines(filters: GuidelineFilters) {
  return useQuery({
    queryKey: guidelineKeys.list(filters),
    queryFn: () => guidelinesApi.list(filters),
    placeholderData: (previous) => previous,
  });
}

/**
 * Change a criterion or turn it off and on (contract v10.35). Not behind the password of
 * the elevated mode: the API does not ask for it (the author of a note turns their own off
 * from the email panel), and every change is still a line in the access log. The lists
 * and the counts are read again, and the email details, whose `note_learning` may name it.
 */
export function useUpdateGuideline() {
  const queryClient = useQueryClient();
  return useMutation<Guideline, ApiError, { id: string; change: GuidelineUpdate }>({
    mutationFn: ({ id, change }) => guidelinesApi.update(id, change),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: guidelineKeys.all });
      void queryClient.invalidateQueries({ queryKey: emailsRootKey });
    },
  });
}

/**
 * Delete a criterion for good (contract v10.36). Behind the password of the elevated mode,
 * like deleting a mailbox: the API asks for it, and there is no way back. The email details
 * are read again, because their «por qué» and `note_learning` named it.
 */
export function useDeleteGuideline(onSuccess?: () => void) {
  return useGuarded<void, string>(
    guidelinesApi.remove,
    [guidelineKeys.all, emailsRootKey],
    onSuccess,
  );
}
